import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SalonStockerHttpTypes } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useSellersMap } from "@/queries/sellers";
import {
  formattedSellerOfferOverviewData,
  getProductDisplayTitle,
  getProductSupplierOffers,
  getVariantPriceOverview,
  getVariantSupplierOffers
} from "@/utils/product";
import {
  combineOfferPriceStates,
  type OfferPriceResolver,
  type OfferPriceState
} from "@/utils/offerPriceState";
import { resolveVariantTitle } from "@/utils/variant";
import type { ProductStatus } from "@medusajs/types";
import {
  ArrowDown,
  Bell,
  ChevronDown,
  Package,
  Shuffle,
  Star
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { AddOfferButton } from "./AddOfferButton";
import { AnimatedPrice } from "./AnimatedPrice";
import { PriceStatusSlot } from "./OfferPriceStatus";
import { PriceInfo } from "./PriceInfo";
import { ProductThumbnailImage } from "./ProductThumbnailImage";
import { useProductDetailDialog } from "./product-detail-dialog/ProductDetailDialogContext";
import {
  getVariantTabConfig,
  isMultiVariantProductType
} from "./product-detail-dialog/variantTabConfig";

function ProductTags<T extends { value: string }>({ tags }: { tags: T[] }) {
  return (
    <div className="mb-2 flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge
          key={tag.value}
          variant="secondary"
          className="h-4 border-purple-300 bg-purple-50 px-1 text-[9px] text-gray-700 backdrop-blur-sm sm:h-5 sm:px-2 sm:text-[10px] lg:h-4 lg:text-[9px] xl:h-5 xl:text-xs"
        >
          {tag.value}
        </Badge>
      ))}
    </div>
  );
}

function ProductRating({
  rating,
  reviews
}: {
  rating: number;
  reviews: number;
}) {
  return (
    <div className="flex items-center">
      <Star className="mr-1 h-2.5 w-2.5 fill-yellow-400 text-yellow-400 sm:h-3 sm:w-3" />
      <span>
        {rating} ({reviews})
      </span>
    </div>
  );
}

function DialogProductOutOfStock(props: { trigger: React.ReactNode }) {
  const handleNotificationSignup = () => {
    console.log("handleNotificationSignup");
  };
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{props.trigger}</TooltipTrigger>
        <TooltipContent
          className="animate-in fade-in-0 zoom-in-95 max-w-sm border-2 border-red-200 bg-gradient-to-br from-red-50 to-orange-50 p-0 shadow-xl"
          side="top"
          align="center"
          sideOffset={8}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                <Package className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="mb-2 text-sm font-semibold text-red-800">
                  Out of Stock
                </h4>
                <p className="mb-3 text-sm leading-relaxed text-gray-700">
                  This item is currently unavailable from this supplier.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Shuffle className="h-3 w-3 text-blue-500" />
                    <span>Try a different supplier above</span>
                  </div>
                  <Button
                    variant="link"
                    className="flex cursor-pointer items-center gap-2 text-xs text-gray-600 transition-colors hover:text-purple-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNotificationSignup();
                    }}
                  >
                    <Bell className="h-3 w-3 text-purple-500" />
                    <span className="underline">
                      Get notified when back in stock
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * How much of a card to render. Deliberately a *prop*, not a viewport query
 * inside the card: the card's width no longer follows the viewport's. At `xl`
 * the results become a 2-up grid, so each cell is roughly half the column - a
 * card there is narrower than the same card on a laptop, and narrower than one
 * on a phone. A `lg:`-gated layout would pick the widest form for the narrowest
 * cell. The caller knows the column it is laying out; the card doesn't.
 * See storefront#145 (P4).
 */
export type CardDensity =
  /** Stacked, one supplier at rest behind `Compare N suppliers`. Phone, and each `xl` grid cell. */
  | "compact"
  /** The two-pane card with the full supplier table, capped. Single-column `lg`. */
  | "full"
  /**
   * The stacked card of `compact`, but with the tabular supplier rows
   * (delivery, stock and price on the one line). Each `xl` grid cell.
   */
  | "twoUp";

/**
 * How many supplier rows a card shows at rest, per density.
 *
 * A product commonly carries ~11 suppliers, and rendering all of them inline is
 * what made a single card fill a phone screen. Both densities cap; they differ
 * in how hard. `full` keeps the rich table and hides the tail behind
 * `Show N more suppliers`; `compact` shows only the offer the card is selling.
 */
const VISIBLE_SUPPLIER_ROWS: Record<CardDensity, number> = {
  compact: 1,
  full: 3,
  twoUp: 3
};

type SupplierPriceComparisonTableProps = {
  productId: string;
  productType?: string;
  density: CardDensity;
  /**
   * Product-level supplier offers (one best offer per seller, aggregated across
   * all variants) - so the table matches the sidebar `sellers[]` facet. Built by
   * `getProductSupplierOffers`. See salonstocker/storefront#82.
   */
  sellerOffers: SalonStockerHttpTypes.StoreSellerOffer[];
  productName: string;
  thumbnail: string | null;
  /**
   * The selected variant's display name (e.g. "5G"), shown next to the
   * header when `sellerOffers` has been narrowed to one variant instead of
   * the product-wide aggregate. `undefined` in the aggregate ("All
   * <variants>") state.
   */
  variantLabel?: string;
  /**
   * Whether a seller's offer may show its price yet. Absent means every offer
   * can (guests, and callers that do not resolve per-salon pricing). See
   * `OfferPriceState`.
   */
  offerPriceState?: OfferPriceResolver;
};

const PriceLinePriceInfo = (props: {
  className?: string;
  /** Anything but `ready` replaces the price with a loader or an error. */
  priceState?: OfferPriceState;
  originalPriceFormatted?: string;
  calculatedPriceFormatted?: string;
  enableAnimation?: boolean;
  /**
   * Float the struck-out was-price above the price instead of setting it inline
   * before it. Used by the desktop comparison rows: inline, the pair is ~40px
   * wider than a lone price, and since only discounted offers have a was-price
   * the price column had to reserve that width on every row - which showed up
   * as a big, uneven gap after STOCK on the rows without one. Stacked, the
   * column is never wider than a single price.
   *
   * The was-price is taken out of flow rather than stacked in it, so the price
   * itself is still a single line and centres on exactly the same axis as the
   * rest of the row. In flow the two-line block centres as a unit and drops the
   * price ~4px below everything else.
   */
  stacked?: boolean;
}) => {
  const {
    className,
    originalPriceFormatted,
    calculatedPriceFormatted,
    enableAnimation,
    stacked,
    priceState = "ready"
  } = props;

  if (priceState !== "ready") {
    return (
      <div
        className={stacked ? `flex justify-end ${className ?? ""}` : className}
      >
        <PriceStatusSlot state={priceState} showLabel={!stacked} />
      </div>
    );
  }

  if (stacked) {
    return (
      <div className={`relative flex justify-end ${className ?? ""}`}>
        {originalPriceFormatted && (
          <span className="absolute bottom-full right-0 whitespace-nowrap text-[9px] leading-none text-gray-400 line-through xl:text-[10px]">
            {enableAnimation ? (
              <AnimatedPrice
                value={originalPriceFormatted}
                highlightOnChange={false}
              />
            ) : (
              originalPriceFormatted
            )}
          </span>
        )}
        <span className="whitespace-nowrap font-bold text-purple-800">
          {enableAnimation ? (
            <AnimatedPrice
              value={calculatedPriceFormatted || ""}
              highlightOnChange={true}
            />
          ) : (
            calculatedPriceFormatted
          )}
        </span>
      </div>
    );
  }

  return (
    <div className={className}>
      {originalPriceFormatted && (
        <span className="mr-1 text-gray-400 line-through">
          {enableAnimation ? (
            <AnimatedPrice
              value={originalPriceFormatted}
              highlightOnChange={false}
            />
          ) : (
            originalPriceFormatted
          )}
        </span>
      )}
      <span className="font-bold text-purple-800">
        {enableAnimation ? (
          <AnimatedPrice
            value={calculatedPriceFormatted || ""}
            highlightOnChange={true}
          />
        ) : (
          calculatedPriceFormatted
        )}
      </span>
    </div>
  );
};

type SupplierPriceLineProps = {
  offerId: string;
  variantId: string;
  status: SalonStockerHttpTypes.StoreSellerOfferStatus;
  handlingTimeLabel: string;
  priceCalculatedFormatted: string;
  priceOriginalFormatted?: string;
  sellerName: string;
  priceState?: OfferPriceState;
  //
  isAddedToCart?: boolean;
  onOpenDialog?: () => void;
};

/**
 * The stacked supplier row - seller and stock badge on one line, delivery and
 * price under it, add button on the right. Used by the `compact` density at
 * every width, so it carries no viewport gate of its own.
 */
const CompactSupplierRow = React.memo((props: SupplierPriceLineProps) => {
  const { onOpenDialog } = props;

  const isAvailableBySeller =
    props.status === SalonStockerHttpTypes.StoreSellerOfferStatus.ACTIVE;

  const handleClick = () => {
    if (isAvailableBySeller && onOpenDialog) {
      onOpenDialog();
    }
  };

  const badgeNode = (
    <Badge
      variant={isAvailableBySeller ? "success" : "critical"}
      className="h-4 px-1.5 py-0.5 text-[9px] sm:text-[10px]"
    >
      {isAvailableBySeller ? "In Stock" : "Out of Stock"}
    </Badge>
  );

  const buttonNode = (
    <AddOfferButton
      size="md"
      disabled={!isAvailableBySeller}
      label={
        isAvailableBySeller
          ? `Add ${props.sellerName}'s offer to cart`
          : `${props.sellerName} is out of stock`
      }
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
    />
  );

  const priceLineNode = (
    <div
      className={`group flex min-h-[65px] items-center justify-between p-3 ${
        isAvailableBySeller
          ? "cursor-pointer transition-colors hover:bg-gradient-to-r hover:from-amber-200/30 hover:to-purple-50 hover:text-gray-800"
          : "out-of-stock-stripes cursor-not-allowed"
      }`}
      onClick={handleClick}
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <TruncateTooltip className="min-w-0 text-xs font-medium text-gray-700 sm:text-sm">
            {props.sellerName}
          </TruncateTooltip>
          <span className="shrink-0">{badgeNode}</span>
        </div>
        <div className="text-muted-foreground flex items-center gap-3 text-[10px] sm:text-xs">
          <span>{props.handlingTimeLabel}</span>
          <span>•</span>
          <PriceLinePriceInfo
            priceState={props.priceState}
            calculatedPriceFormatted={props.priceCalculatedFormatted}
            originalPriceFormatted={props.priceOriginalFormatted}
            enableAnimation
          />
        </div>
      </div>
      {buttonNode}
    </div>
  );

  if (!isAvailableBySeller) {
    return <DialogProductOutOfStock trigger={priceLineNode} />;
  }
  return priceLineNode;
});

CompactSupplierRow.displayName = "CompactSupplierRow";

/**
 * Track sizes and column gap for the desktop supplier row, shared with the
 * column header so the two can never drift apart.
 *
 * The delivery / stock / price / add-button columns are sized to their widest
 * content (measured, incl. the header labels - `DELIVERY` is wider than any
 * value) rather than given `fr` fractions. With fractions the leftover width
 * landed inside the tracks, so every gap was really "gap + whatever that column
 * had spare" and the four items read as unevenly spaced. Fixed tracks mean each
 * gap is just the shared column gap.
 *
 * Delivery and stock are centred in their tracks; the price is right-aligned so
 * it ends flush against the button on every row. The price track only has to
 * fit a single price because the was-price is stacked above it (see
 * `PriceLinePriceInfo`) - inline it needed ~40px more, which every row paid for
 * whether or not it had a discount.
 *
 * Only the supplier column flexes, so all the leftover width shows up as one
 * deliberate break between the supplier name and the cluster.
 */
const SUPPLIER_ROW_GRID_COLUMNS =
  "gap-6 grid-cols-[minmax(0,1fr)_0px_2.625rem_2.25rem_2.75rem_1.375rem] xl:grid-cols-[minmax(0,1fr)_0px_3rem_2.375rem_3.5rem_1.5rem]";

/**
 * The `twoUp` price summary, laid out on the *same grid tracks* as the supplier
 * rows below it (see {@link SUPPLIER_ROW_GRID_COLUMNS}). Aligning the two by
 * padding arithmetic drifted whenever a track changed; sharing the tracks makes
 * the summary price end on exactly the same edge as the PRICE column, and the
 * savings pill sit on the same tracks, by construction.
 *
 * The image and title block takes the flexible first track, so the price
 * summary shares its height. `FROM` stacks above the price in the same column. On the price's baseline,
 * the was-price and the savings pill sit in the delivery and stock tracks to
 * its left, in that order.
 */
function TwoUpPriceRow({
  priceMinFormatted,
  priceMaxFormatted,
  priceSavingFormatted,
  priceState = "ready",
  children
}: {
  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
  priceState?: OfferPriceState;
  /** The image and title block, in the flexible first track beside the prices. */
  children: React.ReactNode;
}) {
  return (
    <div
      className={`-mx-3 grid grid-rows-[1fr_auto] items-baseline gap-y-1 px-3 sm:-mx-4 ${SUPPLIER_ROW_GRID_COLUMNS}`}
    >
      {/* Shares the rows with the price cells, so the prices sit beside the
          thumbnail's bottom edge instead of adding a strip of empty height
          under the title. `sm:pl-1` keeps the image on the pane's 16px inset.
          Spans every track, not just the flexible one: the fixed price tracks
          plus their gaps are ~260px, and confining the title to track 1 left
          that as dead space while the name wrapped a word per line. The price
          cells overlay the block's bottom-right, which holds only the short
          category line, never the title. */}
      <div className="col-span-6 col-start-1 row-span-2 row-start-1 self-stretch sm:pl-1">
        {children}
      </div>
      <span className="col-start-5 row-start-1 self-end justify-self-end text-[10px] font-medium uppercase leading-none tracking-wide text-gray-500">
        From
      </span>
      {/* One right-aligned cluster ending on the PRICE column's edge. Its own
          gap, not the tracks', sets the space before the price: the price is
          wider than its track below `xl`, so track-relative cells crowded it. */}
      <div className="col-span-4 col-start-2 row-start-2 flex items-center gap-2 justify-self-end">
        {priceState !== "ready" && <PriceStatusSlot state={priceState} />}
        {priceState === "ready" && priceMaxFormatted && (
          <span className="whitespace-nowrap text-xs leading-none text-gray-400 line-through">
            <AnimatedPrice value={priceMaxFormatted} highlightOnChange={true} />
          </span>
        )}
        {priceState === "ready" && priceSavingFormatted && (
          <span className="inline-flex h-5 items-center whitespace-nowrap rounded-full border border-green-600 bg-transparent pl-1.5 pr-2 text-xs font-semibold leading-none text-green-700">
            <ArrowDown aria-hidden="true" className="mr-0.5 h-3 w-3" />
            {priceSavingFormatted}
          </span>
        )}
        {priceState === "ready" && (
          <span className="whitespace-nowrap text-lg font-bold leading-none text-purple-800">
            <AnimatedPrice value={priceMinFormatted} highlightOnChange={true} />
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The tabular supplier row, aligned to {@link SUPPLIER_ROW_GRID_COLUMNS}. Used
 * by the `full` density only, so - like its compact sibling - it is the
 * caller's density, not the viewport, that decides whether it renders.
 */
const TableSupplierRow = React.memo((props: SupplierPriceLineProps) => {
  const { onOpenDialog } = props;

  const isAvailableBySeller =
    props.status === SalonStockerHttpTypes.StoreSellerOfferStatus.ACTIVE;

  const handleClick = () => {
    if (isAvailableBySeller && onOpenDialog) {
      onOpenDialog();
    }
  };

  const badgeNode = (
    <Badge
      variant={isAvailableBySeller ? "success" : "critical"}
      className="h-4 px-1.5 py-0.5 text-[9px] xl:h-5 xl:text-[10px]"
    >
      {isAvailableBySeller ? "Stock" : "Out"}
    </Badge>
  );

  const buttonNode = (
    <AddOfferButton
      size="sm"
      disabled={!isAvailableBySeller}
      label={
        isAvailableBySeller
          ? `Add ${props.sellerName}'s offer to cart`
          : `${props.sellerName} is out of stock`
      }
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
    />
  );

  const content = (
    <div
      className={`group grid items-center px-3 py-2 xl:py-2.5 ${SUPPLIER_ROW_GRID_COLUMNS} ${
        isAvailableBySeller
          ? "cursor-pointer transition-colors hover:bg-gradient-to-r hover:from-amber-200/30 hover:to-purple-50 hover:text-gray-800"
          : "out-of-stock-stripes cursor-not-allowed"
      }`}
      onClick={handleClick}
    >
      <div className="flex min-w-0 items-center">
        <div className="mr-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-purple-700 to-pink-600 xl:h-2 xl:w-2"></div>
        <TruncateTooltip className="min-w-0 text-xs font-medium text-gray-700 xl:text-sm">
          {props.sellerName}
        </TruncateTooltip>
      </div>
      <div></div>
      <div className="text-muted-foreground whitespace-nowrap text-center text-[10px] xl:text-xs">
        {props.handlingTimeLabel}
      </div>
      <div className="flex justify-center">{badgeNode}</div>
      <PriceLinePriceInfo
        stacked
        priceState={props.priceState}
        // A step above the delivery label and level with the supplier name:
        // the price is what the row is compared on, so it can't be the
        // smallest thing in it. See {@link SUPPLIER_ROW_GRID_COLUMNS} for the
        // track this has to fit.
        className="text-xs xl:text-sm"
        calculatedPriceFormatted={props.priceCalculatedFormatted}
        originalPriceFormatted={props.priceOriginalFormatted}
        enableAnimation={true}
      />

      <div className="flex justify-center">{buttonNode}</div>
    </div>
  );

  if (!isAvailableBySeller) {
    return <DialogProductOutOfStock trigger={content} />;
  }

  return content;
});
TableSupplierRow.displayName = "TableSupplierRow";

function SupplierPriceComparisonTable({
  productId,
  productType,
  density,
  sellerOffers,
  productName,
  thumbnail,
  variantLabel,
  offerPriceState
}: SupplierPriceComparisonTableProps) {
  const { data: sellersMap } = useSellersMap();
  const { openDialog } = useProductDetailDialog();
  // Whether this product TYPE uses the bulk shade-matrix ordering dialog
  // (e.g. "color_with_variants") - distinct from whether THIS product
  // happens to have more than one variant, which is what gates the card's
  // own variant selector above (see `ProductCardWithPriceComparison`).
  const opensShadeMatrixDialog = isMultiVariantProductType(productType);

  const [showAllSuppliers, setShowAllSuppliers] = useState(false);

  // Narrowing to a variant (or back to the aggregate) swaps the offer list for
  // a different one, so an expansion the buyer opened over the old list has
  // nothing to say about the new one - it would silently expand a list they
  // never asked to see. Keyed on the label rather than the array because
  // `sellerOffers` is a fresh array on every parent render.
  useEffect(() => {
    setShowAllSuppliers(false);
  }, [variantLabel]);

  const cap = VISIBLE_SUPPLIER_ROWS[density];
  const isCapped = sellerOffers.length > cap;
  const visibleOffers =
    showAllSuppliers || !isCapped ? sellerOffers : sellerOffers.slice(0, cap);

  // `sellerOffers` arrives cheapest-first, so the rows kept by the cap are the
  // lowest prices - and at `compact` the single survivor is the cheapest offer.
  const hiddenCount = sellerOffers.length - cap;
  const expanderLabel = showAllSuppliers
    ? "Show fewer"
    : density === "compact"
      ? `Compare ${sellerOffers.length} suppliers`
      : `Show ${hiddenCount} more supplier${hiddenCount === 1 ? "" : "s"}`;

  return (
    <div className={density === "full" ? "col-span-7 p-0" : "p-0"}>
      {/* No column header at `compact`. It said "Available Suppliers" over a
          list whose rows are already labelled, and the ~28px it cost came off
          the one surface with no height to spare - the phone, where the AC is
          two products per screen. The variant it named is still on screen: the
          selector in the panel above shows it. */}
      {density !== "compact" && (
        /* px matches the rows below so the labels line up with their columns. */
        <div className="bg-gradient-to-r from-pink-50 via-neutral-100 to-neutral-100 px-3 py-1.5 text-gray-700 xl:py-2">
          {variantLabel && (
            <TruncateTooltip
              as="p"
              className="mb-1 text-[10px] font-medium text-purple-700 xl:text-xs"
            >
              Showing suppliers for {variantLabel}
            </TruncateTooltip>
          )}
          <div
            className={`grid text-[10px] font-medium xl:text-xs ${SUPPLIER_ROW_GRID_COLUMNS}`}
          >
            <div>SUPPLIER</div>
            <div></div>
            <div className="text-center">DELIVERY</div>
            <div className="text-center">STOCK</div>
            <div className="text-right">PRICE</div>
            <div></div>
          </div>
        </div>
      )}

      {sellerOffers.length === 0 && (
        <p className="px-3 py-6 text-center text-xs text-gray-500 xl:px-4">
          No suppliers currently stock{" "}
          {variantLabel ? variantLabel : "this product"}.
        </p>
      )}

      <div className="divide-border divide-y">
        {visibleOffers.map((offer) => {
          const formattedSellerOffersProps = formattedSellerOfferOverviewData(
            offer,
            sellersMap
          );

          const handleOpenDialog = () => {
            // Multi-variant PRODUCT TYPES open the shade-matrix grid locked
            // to the clicked supplier; everything else uses the single-offer
            // flow.
            if (opensShadeMatrixDialog) {
              openDialog({
                offerId: formattedSellerOffersProps.offerId,
                sellerName: formattedSellerOffersProps.sellerName,
                productName,
                thumbnail,
                productId,
                productType,
                sellerId: offer.seller_id
              });
              return;
            }

            openDialog({
              offerId: formattedSellerOffersProps.offerId,
              sellerName: formattedSellerOffersProps.sellerName,
              productName,
              thumbnail,
              pricingProductId: productId
            });
          };

          const priceState = offerPriceState?.(offer) ?? "ready";

          return density === "compact" ? (
            <CompactSupplierRow
              key={offer.id}
              {...formattedSellerOffersProps}
              priceState={priceState}
              onOpenDialog={handleOpenDialog}
            />
          ) : (
            <TableSupplierRow
              key={offer.id}
              {...formattedSellerOffersProps}
              priceState={priceState}
              onOpenDialog={handleOpenDialog}
            />
          );
        })}
      </div>

      {isCapped && (
        <button
          type="button"
          aria-expanded={showAllSuppliers}
          onClick={() => setShowAllSuppliers((shown) => !shown)}
          className={`flex w-full cursor-pointer items-center justify-between gap-2 border-t border-gray-100 px-3 text-xs font-semibold text-purple-700 transition-colors hover:bg-purple-50 ${
            // The compact form is the one a finger drives, so its expander
            // carries the 44px target; the table's sits in a dense desktop row.
            density === "compact" ? "min-h-[44px] py-2.5" : "py-2"
          }`}
        >
          <span>{expanderLabel}</span>
          <ChevronDown
            aria-hidden="true"
            className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${
              showAllSuppliers ? "rotate-180" : ""
            }`}
          />
        </button>
      )}
    </div>
  );
}

/** The sentinel `<select>` value for "every variant, aggregated" - not a real variant id. */
const ALL_VARIANTS_ID = "__all__";

type CardVariant = Pick<
  SalonStockerHttpTypes.StoreProductVariantPreview,
  "id" | "title" | "options" | "sku" | "seller_offers"
>;

function variantOptionLabel(variant: CardVariant): string {
  // Medusa's placeholder title names nothing the buyer chose, so it falls
  // through to the option values (or SKU) that actually identify the variant.
  const title = resolveVariantTitle(variant.title);
  if (title) return title;
  const code = variant.options
    .map((option) => option.value)
    .filter(Boolean)
    .join(" · ");
  return code || variant.sku;
}

/**
 * The dropdown that narrows the card from the product-wide supplier
 * aggregate down to one variant's own offers (e.g. one shade). Only rendered
 * when the product has more than one variant - see
 * `ProductCardWithPriceComparison`.
 */
function VariantSelector({
  variants,
  selectedVariantId,
  allLabel,
  onChange
}: {
  variants: CardVariant[];
  selectedVariantId: string;
  allLabel: string;
  onChange: (variantId: string) => void;
}) {
  return (
    <div className="relative mb-2 inline-block max-w-full">
      <select
        value={selectedVariantId}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        aria-label="Select variant"
        className="w-full max-w-[9.5rem] cursor-pointer appearance-none truncate rounded-md border border-purple-200 bg-white/80 py-0.5 pl-2 pr-5 text-[10px] font-medium text-purple-700 sm:text-xs lg:text-[10px] xl:text-xs"
      >
        <option value={ALL_VARIANTS_ID}>{allLabel}</option>
        {variants.map((variant) => {
          const isAvailable = variant.seller_offers.length > 0;
          return (
            <option
              key={variant.id}
              value={variant.id}
              disabled={!isAvailable}
              className={isAvailable ? undefined : "text-gray-400"}
            >
              {variantOptionLabel(variant)}
              {isAvailable ? "" : " (Not Available)"}
            </option>
          );
        })}
      </select>
      <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-purple-400" />
    </div>
  );
}

export type ProductCardWithPriceComparisonProps = {
  /**
   * The product ID
   */
  productId: string;
  /**
   * Product's status
   */
  status: ProductStatus;
  variantId: string;
  offerId: string;
  title: string;
  brand: string;
  thumbnail: string | null;
  category: string;
  /** The product's type value (e.g. "color_with_variants"), if any. */
  productType?: string;

  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
  sellerName: string;

  rating?: number;
  reviews?: number;
  tags?: string[];

  /**
   * Every variant of the product, used both by the variant selector (its
   * dropdown options) and to derive `sellerOffers`/price for whichever
   * variant is selected. When there's only one, no selector is shown and the
   * card behaves as before.
   */
  variants: CardVariant[];
  /**
   * The suppliers currently selected in the sidebar filter, by seller id -
   * pinned to the top of the comparison table regardless of which variant
   * (or "All") is selected. See salonstocker/storefront#82.
   */
  pinnedSellerIds?: ReadonlySet<string>;
  /**
   * The variant id to preselect, e.g. because the search query named a shade
   * precisely (see `findVariantMatchingSearchQuery`). Falls back to the "All
   * variants" aggregate state when unset or when it doesn't match any
   * variant.
   */
  defaultVariantId?: string;
  /**
   * Whether a seller's offer may show its price yet (KAN-343): `loading` while
   * a claimed seller's trade price is still on its way, `error` if it never
   * arrives. Unset means every offer can, as for a guest. The card holds its
   * price range back whenever an offer in it cannot show a price.
   */
  offerPriceState?: OfferPriceResolver;
  /**
   * How much of the comparison to render. See {@link CardDensity} - the caller
   * decides, because the card's width no longer tracks the viewport's.
   */
  density: CardDensity;
};

const ProductCardWithPriceComparison = React.memo(
  (props: React.PropsWithChildren<ProductCardWithPriceComparisonProps>) => {
    const {
      productId,
      productType,
      status,
      title,
      brand,
      thumbnail,
      category,
      rating,
      reviews,
      tags,
      priceMinFormatted,
      priceMaxFormatted,
      priceSavingFormatted,
      variants,
      pinnedSellerIds,
      defaultVariantId,
      offerPriceState,
      density
    } = props;

    const [selectedVariantId, setSelectedVariantId] = useState(
      defaultVariantId ?? ALL_VARIANTS_ID
    );

    // Re-sync whenever the search-matched default changes (e.g. the buyer
    // refines their query to name a different shade) - without this, a card
    // that survives the re-render (same product, new query) would keep
    // showing whichever variant a stale prior query had matched.
    useEffect(() => {
      setSelectedVariantId(defaultVariantId ?? ALL_VARIANTS_ID);
    }, [defaultVariantId]);

    // `selectedVariantId` only ever holds a real variant id when the
    // selector is rendered (`variants.length > 1`) - it's seeded from
    // `defaultVariantId`/`findVariantMatchingSearchQuery` (which itself
    // requires >1 variant) and otherwise only settable via that selector.
    // So a plain lookup already resolves to `undefined` in every other case
    // (the sentinel `ALL_VARIANTS_ID` matches no real variant).
    const selectedVariant = variants.find((v) => v.id === selectedVariantId);

    // The helpers order active-first with pinned suppliers leading; the card
    // wants a plain price comparison, lowest first. Array.sort is stable, so
    // equal prices keep the helpers' order (in stock before out of stock).
    const sellerOffers = useMemo(() => {
      const offers = selectedVariant
        ? getVariantSupplierOffers(selectedVariant, pinnedSellerIds)
        : getProductSupplierOffers({ variants }, pinnedSellerIds);
      return [...offers].sort(
        (a, b) =>
          a.calculated_price.calculated_amount -
          b.calculated_price.calculated_amount
      );
    }, [selectedVariant, variants, pinnedSellerIds]);

    const priceOverview = useMemo(
      () =>
        selectedVariant
          ? getVariantPriceOverview(selectedVariant)
          : { priceMinFormatted, priceMaxFormatted, priceSavingFormatted },
      [
        selectedVariant,
        priceMinFormatted,
        priceMaxFormatted,
        priceSavingFormatted
      ]
    );

    // The range's min and max span every offer shown, so one offer that cannot
    // show its price holds the whole range back.
    const rangeOffers = selectedVariant
      ? selectedVariant.seller_offers
      : variants.flatMap((variant) => variant.seller_offers);
    const rangePriceState = offerPriceState
      ? combineOfferPriceStates(
          rangeOffers.map((offer) => offerPriceState(offer))
        )
      : "ready";

    const variantConfig = getVariantTabConfig(productType);
    const allVariantsLabel = variantConfig
      ? `All ${variantConfig.labelDefault}`
      : "All Variants";
    const displayTitle = getProductDisplayTitle({ title, brand });

    const priceInfo = (
      <PriceInfo //
        align={density === "compact" ? "end" : "start"}
        className={density === "compact" ? "pr-10" : undefined}
        priceMinFormatted={priceOverview.priceMinFormatted}
        priceMaxFormatted={priceOverview.priceMaxFormatted}
        priceSavingFormatted={priceOverview.priceSavingFormatted}
        priceState={rangePriceState}
        enableAnimation
      />
    );

    const paneBody = (
      <div className="flex h-full">
        {/* Product Image. Smaller at `compact`: that form has to fit two
                  products on a phone screen and each cell of the `xl` grid, and
                  the thumbnail is the one element that costs the same height
                  whatever the width. */}
        <div
          className={`mr-3 flex flex-shrink-0 items-center justify-center rounded-lg bg-white/95 shadow-sm backdrop-blur ${
            density === "full"
              ? "h-32 w-32 xl:h-40 xl:w-40"
              : "h-20 w-20 sm:h-24 sm:w-24"
          }`}
        >
          <ProductThumbnailImage
            src={thumbnail}
            alt={title}
            className="h-full w-full object-cover"
            fallback={
              <Package className="h-6 w-6 text-purple-600 sm:h-8 sm:w-8 lg:h-8 lg:w-8 xl:h-10 xl:w-10" />
            }
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1">
            <h3
              className={`mb-1 font-bold leading-tight ${
                density === "full"
                  ? "text-sm xl:text-lg"
                  : "text-sm sm:text-base"
              }`}
            >
              {displayTitle}
            </h3>

            {variants.length > 1 && (
              <VariantSelector
                variants={variants}
                selectedVariantId={
                  selectedVariant ? selectedVariant.id : ALL_VARIANTS_ID
                }
                allLabel={allVariantsLabel}
                onChange={setSelectedVariantId}
              />
            )}

            <div
              className={`mb-2 flex flex-wrap items-center gap-2 text-[10px] sm:gap-3 ${
                density === "full" ? "xl:text-xs" : "sm:text-xs"
              }`}
            >
              {rating && reviews && (
                <>
                  <ProductRating rating={rating} reviews={reviews} />
                  <span className="hidden opacity-70 sm:inline">•</span>
                </>
              )}
              <span>{category}</span>
            </div>

            {/* Tags */}
            {/* {tags?.length ? <ProductTags tags={tags} /> : null} */}
          </div>

          {density === "compact" && priceInfo}
        </div>
      </div>
    );

    return (
      <Card
        className={`overflow-hidden border-purple-200/30 shadow-sm transition-all duration-300 hover:shadow-md ${
          // Spans two rows of the results grid and takes them as its own
          // (subgrid), so the header and the supplier table line up with the
          // neighbouring card however many lines either title wraps to.
          density === "twoUp"
            ? "row-span-2 grid grid-rows-[subgrid] gap-y-0"
            : ""
        }`}
      >
        <div
          className={
            density === "full"
              ? "grid grid-cols-12 gap-0"
              : density === "twoUp"
                ? "contents"
                : "flex flex-col gap-0"
          }
        >
          {/* Left Side - Product Card */}
          <div
            className={`bg-radial-[at_25%_75%] from-white via-purple-50 to-pink-50 text-black ${
              density === "full" ? "col-span-5 p-4" : "p-3 sm:p-4"
            }`}
          >
            {density === "twoUp" ? (
              <TwoUpPriceRow
                priceMinFormatted={priceOverview.priceMinFormatted}
                priceMaxFormatted={priceOverview.priceMaxFormatted}
                priceSavingFormatted={priceOverview.priceSavingFormatted}
                priceState={rangePriceState}
              >
                {paneBody}
              </TwoUpPriceRow>
            ) : density === "full" ? (
              // Under the image and title, on the pane's full width: beside them
              // the text column is too narrow at `lg` and the pill ran into the
              // supplier table.
              <div className="flex h-full flex-col justify-between gap-3">
                {paneBody}
                {priceInfo}
              </div>
            ) : (
              paneBody
            )}
          </div>

          {/* Right Side - Supplier Table */}
          <SupplierPriceComparisonTable
            productId={productId}
            productType={productType}
            density={density}
            sellerOffers={sellerOffers}
            offerPriceState={offerPriceState}
            productName={displayTitle}
            thumbnail={thumbnail}
            variantLabel={
              selectedVariant ? variantOptionLabel(selectedVariant) : undefined
            }
          />
        </div>
      </Card>
    );
  }
);
ProductCardWithPriceComparison.displayName = "ProductCardWithPriceComparison";

export { ProductCardWithPriceComparison };
